// src/com/rtd_term_pos/RtdTermPosDict.js
import { AppObject, AppWord, TableColumnType } from "../../value/AppConst";
import BedtimeIcon from '@mui/icons-material/Bedtime'; // No report at all.
import CellTowerIcon from '@mui/icons-material/CellTower'; // cell position
import GpsOffIcon from '@mui/icons-material/GpsOff'; // no position report 1 day or more
import LocalParkingIcon from '@mui/icons-material/LocalParking'; // no position rep 0.5 hour or more
import GpsFixedIcon from '@mui/icons-material/GpsFixed'; // speed < 2
import NavigationIcon from '@mui/icons-material/Navigation'; // speed >= 2
import MarkerUtil from "../map/MarkerUtil";
import ValueMisc from "../../value/ValueMisc";

const SM = AppWord.SMALL;
const fontSizeStyle = {fontSize:'12pt'}

const MovementMarkDef =  {
    id:'-mv-mark', label:'상태', width:50, show: true,
    option: {
        aligh:'center', type: TableColumnType.GRAPH,
        form: (rec, rowIndx) => {
            if(rec.gpsTime===AppWord.NOT_REAL_GPS_TIME)
                return <BedtimeIcon fontSize={SM} style={fontSizeStyle} color='disabled' />;
            if(rec.reportAgeSec > 432000)
                return <GpsOffIcon fontSize={SM} style={fontSizeStyle} color='warning' />;
            if(rec.reportAgeSec > 86400)
                return <GpsOffIcon fontSize={SM} style={fontSizeStyle} color='inherit' />;
            if(rec.posSys==='C' || rec.posSys===null)
                return <CellTowerIcon fontSize={SM} style={fontSizeStyle} color='disabled' />
            if(rec.reportAgeSec > 1800)
                return <LocalParkingIcon fontSize={SM} style={fontSizeStyle} color='inherit' />
            if(rec.speed < 2)
                return <GpsFixedIcon fontSize={SM} style={fontSizeStyle} color='success'/>;
            return (
                <NavigationIcon fontSize={SM}
                color='primary'
                style={{
                    ...fontSizeStyle,
                    transform: 'rotate('+rec.direction+'deg)'
                }}/>
            );
        }
    }
};

function printTemp(r, i, v) {
    if(v) {
        if(v < -100) return null;
        return v;
    }
    return null;
}

/**
 * RTD_TERM_POS React Component Dictionary for Input and Output
 * Project: NewTOP for cloud
 * 2024-11-15 16:02:14 using DbdbTap
 */
export const RtdTermPosDictionary = {
    errorTitle: {
        Get: '차량위치 조회 오류',
    },
    floatingInfoProps: [ // 지도 테이블의 플로팅 상자에 표시할 항목
        {id: 'vehPlates', label: '차량번호'},
        {id: 'vehAlias', label: '지도표시명'},
        {
            id: 'reportAgeSec',
            label: '최종보고',
            print: (rec, value) => {
                let v = rec.gpsTime;
                if(rec.gpsTime===AppWord.NOT_REAL_GPS_TIME) v = '보고 기록 없음';
                else if(value > 432000) v += ' (5일 이상 보고 없음)';
                else if(value > 86400) v += ' (1일 이상 보고 없음)';
                else if(value > 1800) v += ' (30분 이상 보고 없음)';
                return v;
            }
        },
        {id: 'location', label: '위치'},
        {
            id: 'posSys', label: '위치방식',
            print: (rec, value) => {
                if(value==='C') return '기지국 위치';
                if(value==='L') return '과거 위치';
                return 'GPS 위치';
            }
        },
        {
            id: 'speed', label: '속도(km/h)',
            print: (rec, value) => {
                const dirIdx = MarkerUtil.degreeToIndex16(rec.direction);
                const dirKor = AppObject.directionKorean16[dirIdx];
                return `${value} km/h (${dirKor}향)`;
            }
        },
        {id: 'distKm', label: '누적거리(km)', print: (rec, value) => value ? ValueMisc.float2str(value, 1) : value},
        {id: 'driverName', label: '기사'},
        {id: 'driverMobile', label: '휴대전화'},
        {id: 'tempr1', label: '전면온도', print: (rec, value) => value < -100 ? '-' : `${value}°C`},
        {id: 'tempr3', label: '중면온도', print: (rec, value) => value < -100 ? '-' : `${value}°C`},
        {id: 'tempr2', label: '후면온도', print: (rec, value) => value < -100 ? '-' : `${value}°C`},
        {id: 'comAlias', label: '업체'},
        {id: 'termId', label: '단말기번호'},
    ],
    tableNoReportProps: {
        tableId: 'rtdTermPosTableNoReport',
        uniqueKey: 'vehInfoSeq',
        flexOption: {
            multiChecker:false,
            hideFirstTopButton: true,
            hideColumnConfig: true,
            hideBubble: true,
            growToFill: true,
        },
        columns: [
            { id: 'comName', label: '운송사', width: 140, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'vehPlates', label: '차량번호', width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'driverName', label: '기사', width: 70, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'driverMobile', label: '휴대전화', width: 140, show: true, privacy:true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'gpsTime', label: '최종보고', width: 165, show: true, option:{align: 'inherit', type:TableColumnType.DATETIME} },
        ]
    },
    tablePosAsVehicleProps: {
        tableId: 'rtdTermPosTablePosAsVehicle', // 차량목록 대신하여 차량위치를 표시할 테이블 (경로보기 화면)
        uniqueKey: 'vehInfoSeq',
        flexOption: {
            multiChecker:false,
            hideFirstTopButton: true,
            hideColumnConfig: true,
            hideBubble: false,
            growToFill: true,
        },
        columns: [
            { id: 'vehPlates', label: '차량번호', width: 110, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'gpsTime', label: '최종보고', width: 135, show: true, option:{align: 'inherit', print:(r,i,v)=>v?v.substring(0,16):'-'} },
            { id: 'comName', label: '운송사', width: 140, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
        ]
    },
    tableProps: {
        tableId: 'rtdTermPosTable',
        uniqueKey: 'vehInfoSeq',
        flexOption: {
            multiChecker:false,
            hideFirstTopButton: false,
            hideColumnConfig: true,
            hideBubble: false,
            growToFill: true,
        },
        columns: [
            //print using getTableColumnOption(c,t,i)
            { id: 'vehPlates', label: '차량번호', width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'vehAlias', label: '지도표시명', width: 90, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'gpsTime', label: '최종보고', width: 165, show: true, option:{align: 'inherit', type:TableColumnType.DATETIME} },
            MovementMarkDef,
            { id: 'location', label: '최종위치', width: 360, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'posSys', label: '위치타입', width: 60, show: true, option:{align: 'center', print:(r,i,v)=>v==='C'?'기지국':v==='G'?'GPS':v==='L'?'과거위치':'기타'} },
            { id: 'speed', label: '시속Km', width: 70, show: true, option:{align: 'right', type:TableColumnType.FLOAT} },
            { id: 'distKm', label: '누적거리', width: 100, show: true, option:{align: 'right', type:TableColumnType.FLOAT} },
            { id: 'tempr1', label: '전면', width: 50, show: true, option:{align: 'right', print: printTemp} },
            { id: 'tempr3', label: '중면', width: 50, show: true, option:{align: 'right', print: printTemp} },
            { id: 'tempr2', label: '후면', width: 50, show: true, option:{align: 'right', print: printTemp} },
            { id: 'comAlias', label: '운송사', width: 140, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'driverName', label: '기사', width: 90, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'driverMobile', label: '휴대전화', width: 100, show: true, privacy:true, option:{align: 'center', type:TableColumnType.TEXT} },
            { id: 'vehTypeName', label: '차종', width: 130, show: true, option:{align: 'inherit', print:(r,i,v)=>`${v}-${r.vehTonName}`} },
            { id: 'irActionName', label: '업무보고', width: 220, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'pwrType', label: '전원', width: 120, show: true, option:{align: 'center', print:(r,i,v) => v===1 ? '배터리' : '차량전원'} },
            /* { id: 'comId', label: Dict.comId.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'custId', label: Dict.custId.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'devId', label: Dict.devId.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.INTEGER} },
            { id: 'vehId', label: Dict.vehId.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.INTEGER} },
            { id: 'vehPlates', label: Dict.vehPlates.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'vehAlias', label: Dict.vehAlias.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'gpsTime', label: Dict.gpsTime.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.DATETIME} },
            { id: 'recvTime', label: Dict.recvTime.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.DATETIME} },
            { id: 'insertTime', label: Dict.insertTime.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.DATETIME} },
            { id: 'gpsLat', label: Dict.gpsLat.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'gpsLon', label: Dict.gpsLon.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'xDist', label: Dict.xDist.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.FLOAT} },
            { id: 'yDist', label: Dict.yDist.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.FLOAT} },
            { id: 'location', label: Dict.location.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'direction', label: Dict.direction.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.INTEGER} },
            { id: 'statusCode', label: Dict.statusCode.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'eventCd', label: Dict.eventCd.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.INTEGER} },
            { id: 'svcCondCd', label: Dict.svcCondCd.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'loadCondCd', label: Dict.loadCondCd.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'gpioBits', label: Dict.gpioBits.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.INTEGER} },
            { id: 'paraDiff', label: Dict.paraDiff.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.INTEGER} },
            { id: 'meridCode', label: Dict.meridCode.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.INTEGER} },
            { id: 'cycleMake', label: Dict.cycleMake.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.INTEGER} },
            { id: 'cycleReport', label: Dict.cycleReport.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.INTEGER} },
            { id: 'doorEvent', label: Dict.doorEvent.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.INTEGER} },
            { id: 'door0', label: Dict.door0.label, width: 120, show: true, option:{align: 'center', type:TableColumnType.BOOLEAN} },
            { id: 'door1', label: Dict.door1.label, width: 120, show: true, option:{align: 'center', type:TableColumnType.BOOLEAN} },
            { id: 'door2', label: Dict.door2.label, width: 120, show: true, option:{align: 'center', type:TableColumnType.BOOLEAN} },
            { id: 'tempr4', label: Dict.tempr4.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.FLOAT} },
            { id: 'itinRepType', label: Dict.itinRepType.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.INTEGER} },
            { id: 'irLat', label: Dict.irLat.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.FLOAT} },
            { id: 'irLon', label: Dict.irLon.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.FLOAT} },
            { id: 'irYCoord', label: Dict.irYCoord.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.FLOAT} },
            { id: 'irXCoord', label: Dict.irXCoord.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.FLOAT} },
            { id: 'irDist', label: Dict.irDist.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.FLOAT} },
            { id: 'irMins', label: Dict.irMins.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.FLOAT} },
            { id: 'irPoiType', label: Dict.irPoiType.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.INTEGER} },
            { id: 'irPoiSeq', label: Dict.irPoiSeq.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.FLOAT} },
            { id: 'irAction', label: Dict.irAction.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.INTEGER} },
            { id: 'addrPrefix', label: Dict.addrPrefix.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'poiName', label: Dict.poiName.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'poiDistM', label: Dict.poiDistM.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.INTEGER} },
            { id: 'roadName', label: Dict.roadName.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'roadRank', label: Dict.roadRank.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'roadMaxSpeed', label: Dict.roadMaxSpeed.label, width: 120, show: true, option:{align: 'right', type:TableColumnType.INTEGER} },
            { id: 'midRoute', label: Dict.midRoute.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'vehTypeName', label: Dict.vehTypeName.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'vehTonName', label: Dict.vehTonName.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'driverName', label: Dict.driverName.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'driverMobile', label: Dict.driverMobile.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'vehTonName', label: Dict.vehTonName.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'operFontColor', label: Dict.operFontColor.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'operBackColor', label: Dict.operBackColor.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'noOperFontColor', label: Dict.noOperFontColor.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'noOperBackColor', label: Dict.noOperBackColor.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'offFontColor', label: Dict.offFontColor.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'offBackColor', label: Dict.offBackColor.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'doorSensors', label: Dict.doorSensors.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'comName', label: Dict.comName.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'comAlias', label: Dict.comAlias.label, width: 120, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'regUserName', label: "등록자", width: 80, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} }, */
        ]
    },
    tablePropsForMap: {
        tableId: 'rtdTermPosTableForMap',
        uniqueKey: 'vehInfoSeq',
        flexOption: {
            multiChecker:false,
            hideFirstTopButton: true,
            hideColumnConfig: true,
            hideBubble: true, // 외부에서 MapFloatingInfo를 사용해서 표시
            growToFill: true,
        },
        columns: [
            { id: 'vehAlias', label: '지도표시명', width: 100, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            MovementMarkDef,
            { id: 'gpsTime', label: '최종보고', width: 165, show: true, option:{align: 'inherit', type:TableColumnType.DATETIME} },
            { id: 'driverName', label: '기사', width: 100, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'driverMobile', label: '휴대전화', width: 100, show: true, privacy:true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'location', label: '최종위치', width: 360, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'vehPlates', label: '차량번호', width: 100, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
            { id: 'comAlias', label: '운송사', width: 140, show: true, option:{align: 'inherit', type:TableColumnType.TEXT} },
        ]
    },
};
